import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Upload,
  Download,
  RotateCw,
  Send,
  Trash2,
  Calendar as CalIcon,
  Search,
  ArrowUpDown,
  FileSpreadsheet,
  AlertCircle,
  BarChart3,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";

export default function StoreFiles() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("batch_id");
  const [orderField, setOrderField] = useState("generated");
  const [orderDir, setOrderDir] = useState("desc");

  const [zeroDates, setZeroDates] = useState([]);
  const [zeroOpen, setZeroOpen] = useState(false);
  const [zeroSubmitting, setZeroSubmitting] = useState(false);

  const isAdmin = user?.group === "admin";

  const loadFiles = async () => {
    const params = {};
    if (search && searchField) {
      params.search = search;
      params.search_field = searchField;
    }
    if (orderField) {
      params.order_field = orderField;
      params.order_dir = orderDir;
    }
    const { data } = await api.get(`/stores/${id}/files`, { params });
    setFiles(data);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/stores/${id}`),
      api.get(`/stores/${id}/files`, {
        params: { order_field: "generated", order_dir: "desc" },
      }),
    ])
      .then(([s, f]) => {
        setStore(s.data);
        setFiles(f.data);
      })
      .catch(() => toast.error(t("storeFiles.messages.loadFailed")))
      .finally(() => setLoading(false));
  }, [id, t]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/stores/${id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.batch) {
        if (data.not_uploaded_count > 0 && data.error_count === 0) {
          toast.warning(t("storeFiles.messages.batchNotUploaded", { count: data.not_uploaded_count }));
        } else if (data.error_count === 0) {
          toast.success(t("storeFiles.messages.batchUploaded", { count: data.uploaded_count }));
        } else {
          toast.warning(t("storeFiles.messages.batchPartial", {
            uploaded: data.uploaded_count,
            processed: data.processed_count,
            errors: data.error_count,
          }));
        }
      } else if (data.status === "uploaded") {
        toast.success(t("storeFiles.messages.uploaded", { filename: data.generated_filename }));
      } else if (data.status === "not_uploaded") {
        toast.warning(t("storeFiles.messages.notUploaded", { filename: data.generated_filename }));
      } else {
        toast.error(data.error_message || t("storeFiles.messages.processingFailed"));
      }
      loadFiles();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const downloadOriginal = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download-original`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename || "report.xls";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("storeFiles.messages.sourceUnavailable"));
    }
  };
  const downloadGenerated = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.generated_filename || "report.txt";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("storeFiles.messages.generatedUnavailable"));
    }
  };

  const regenerate = async (f) => {
    toast.loading(t("storeFiles.messages.regenerating"), { id: "rg" });
    try {
      await api.post(`/files/${f.id}/regenerate`);
      toast.dismiss("rg");
      toast.success(t("storeFiles.messages.regenerated"));
      loadFiles();
    } catch (e) {
      toast.dismiss("rg");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reupload = async (f) => {
    toast.loading(t("storeFiles.messages.reuploading"), { id: "ru" });
    try {
      const { data } = await api.post(`/files/${f.id}/reupload`);
      toast.dismiss("ru");
      if (data.ok) toast.success(t("storeFiles.messages.reuploaded"));
      else toast.error(data.message || t("storeFiles.messages.reuploadFailed"));
      loadFiles();
    } catch (e) {
      toast.dismiss("ru");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (f) => {
    if (!window.confirm(t("storeFiles.messages.deleteConfirm"))) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success(t("storeFiles.messages.deleted"));
      loadFiles();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitZeroSales = async () => {
    if (!zeroDates || zeroDates.length === 0) {
      toast.error(t("storeFiles.messages.selectDate"));
      return;
    }
    setZeroSubmitting(true);
    try {
      const dates = zeroDates.map((d) => d.toISOString().slice(0, 10));
      const { data } = await api.post(`/stores/${id}/zero-sales`, { dates });
      const ok = data.results.filter((r) => r.ok).length;
      const fail = data.results.length - ok;
      const notUploaded = data.results.filter((r) => r.status === "not_uploaded").length;
      if (fail === 0 && notUploaded > 0) {
        toast.warning(t("storeFiles.messages.batchNotUploaded", { count: notUploaded }));
      } else if (fail === 0) toast.success(t("storeFiles.messages.zeroSalesUploaded", { count: ok }));
      else toast.warning(t("storeFiles.messages.zeroSalesPartial", { succeeded: ok, failed: fail }));
      setZeroOpen(false);
      setZeroDates([]);
      loadFiles();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setZeroSubmitting(false);
    }
  };

  if (loading) return <div className="pg-card p-10 text-center text-slate-400">{t("common.loading")}</div>;
  if (!store) return null;

  return (
    <div data-testid="store-files-page">
      <Link to="/stores" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4">
        <ArrowLeft size={15} /> {t("storeFiles.backToStores")}
      </Link>
      <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
            {t("storeFiles.breadcrumb")}
          </div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-slate-900">{store.name}</h1>
          <div className="text-sm text-slate-500 mt-1">
            <span className="mono">MID {store.machine_id}</span> · {store.store_type} ·{" "}
            {store.has_gst === "no" ? t("stores.noGst") : store.has_gst === "retail" ? t("stores.gstRetail") : t("stores.gstFnb")}
          </div>
          {isAdmin && store.notes?.trim() && (
            <div className="mt-3 text-sm text-slate-600 whitespace-pre-line leading-6">
              {store.notes}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/stores/${id}/data-analysis`}
            className="px-4 py-2.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold text-sm flex items-center gap-2"
          >
            <BarChart3 size={15} /> {t("dataAnalysis.title")}
          </Link>
          <Dialog open={zeroOpen} onOpenChange={setZeroOpen}>
            <DialogTrigger asChild>
              <button
                className="px-4 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 font-semibold text-sm flex items-center gap-2"
                data-testid="zero-sales-btn"
              >
                <CalIcon size={15} /> {t("storeFiles.zeroSalesBtn")}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{t("storeFiles.zeroDialogTitle")}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 -mt-2">
                {t("storeFiles.zeroDialogDescription")}
              </p>
              <div className="border rounded-lg p-2 bg-slate-50/50">
                <Calendar
                  mode="multiple"
                  selected={zeroDates}
                  onSelect={(d) => setZeroDates(d || [])}
                  className="rounded-md"
                />
              </div>
              <div className="text-xs text-slate-500">
                {t("storeFiles.selectedCount", { count: zeroDates.length })}
              </div>
              <DialogFooter>
                <button
                  type="button"
                  onClick={() => setZeroOpen(false)}
                  className="px-4 py-2 rounded-md border border-slate-200 text-sm"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={submitZeroSales}
                  disabled={zeroSubmitting}
                  className="px-4 py-2 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold disabled:opacity-60"
                  data-testid="zero-sales-submit"
                >
                  {zeroSubmitting ? t("storeFiles.processing") : t("storeFiles.generateReports", { count: zeroDates.length })}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <label
            className={`px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-sm flex items-center gap-2 cursor-pointer ${
              uploading ? "opacity-60 cursor-wait" : ""
            }`}
            data-testid="upload-btn"
          >
            <Upload size={15} />
            {uploading ? t("storeFiles.processing") : t("storeFiles.uploadBtn")}
            <input
              type="file"
              className="hidden"
              accept=".xls,.xlsx"
              disabled={uploading}
              onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
            />
          </label>
        </div>
      </div>

      {isAdmin && store.upload_enabled === false && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
          {t("storeFiles.uploadSuspended")}
        </div>
      )}

      {/* Search + order */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="pg-card p-4">
          <div className="field-section-title mb-3">
            <Search size={14} /> {t("storeFiles.search.title")}
          </div>
          <div className="flex gap-2">
            <select
              className={`${inp} max-w-[170px]`}
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            >
              <option value="batch_id">{t("storeFiles.columns.batchId")}</option>
              <option value="uploaded_date">{t("storeFiles.search.uploadDate")}</option>
            </select>
            <input
              className={inp}
              placeholder={searchField === "batch_id" ? t("storeFiles.search.batchExample") : t("storeFiles.search.dateExample")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              onClick={loadFiles}
              className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm"
            >
              {t("storeFiles.search.action")}
            </button>
          </div>
        </div>
        <div className="pg-card p-4">
          <div className="field-section-title mb-3">
            <ArrowUpDown size={14} /> {t("storeFiles.order.title")}
          </div>
          <div className="flex gap-2">
            <select className={`${inp} max-w-[170px]`} value={orderField} onChange={(e) => setOrderField(e.target.value)}>
              <option value="uploaded_date">{t("storeFiles.columns.uploadDate")}</option>
              <option value="batch_id">{t("storeFiles.columns.batchId")}</option>
              <option value="generated">{t("storeFiles.columns.generated")}</option>
            </select>
            <select className={`${inp} max-w-[140px]`} value={orderDir} onChange={(e) => setOrderDir(e.target.value)}>
              <option value="desc">{t("storeFiles.order.descending")}</option>
              <option value="asc">{t("storeFiles.order.ascending")}</option>
            </select>
            <button
              onClick={loadFiles}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold text-sm"
            >
              {t("storeFiles.order.action")}
            </button>
          </div>
        </div>
      </div>

      {/* Files table */}
      <div className="pg-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50/70">
              <th className="px-5 py-3 font-semibold">{t("storeFiles.columns.uploadDate")}</th>
              <th className="px-5 py-3 font-semibold">{t("storeFiles.columns.batchId")}</th>
              <th className="px-5 py-3 font-semibold">{t("storeFiles.columns.source")}</th>
              <th className="px-5 py-3 font-semibold">{t("storeFiles.columns.origin")}</th>
              <th className="px-5 py-3 font-semibold">{t("storeFiles.columns.generated")}</th>
              <th className="px-5 py-3 font-semibold">{t("storeFiles.columns.status")}</th>
              <th className="px-5 py-3 font-semibold text-right">{t("storeFiles.columns.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {files.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                  <FileSpreadsheet className="mx-auto mb-2 text-slate-300" size={32} />
                  {t("storeFiles.noFiles")}
                </td>
              </tr>
            ) : (
              files.map((f) => {
                const missingSales = isMissingSales(f);
                const origin = normalizeOrigin(f.origin);
                return (
                <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`file-row-${f.id}`}>
                  <td className="px-5 py-3 mono text-xs text-slate-600 whitespace-nowrap">
                    {missingSales ? "-" : formatUploadDate(f.uploaded_at, i18n.resolvedLanguage || i18n.language)}
                  </td>
                  <td className="px-5 py-3 mono text-slate-700">{missingSales ? "-" : f.batch_id || "-"}</td>
                  <td className="px-5 py-3">
                    {missingSales ? (
                      "-"
                    ) : (
                      <span className={`pill ${f.source === "email" ? "pill-info" : ["zero_sale", "store_closed"].includes(f.source) ? "pill-warning" : "pill-success"}`}>
                        {t(`common.sources.${f.source}`, { defaultValue: f.source || "-" })}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {missingSales ? (
                      "-"
                    ) : (
                      <span className={`pill ${origin === "overwritten" ? "pill-warning" : "pill-info"}`}>
                        {t(`storeFiles.origins.${origin}`)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 mono text-xs text-slate-600 max-w-[200px] truncate" title={f.generated_filename}>
                    {f.generated_filename || "—"}
                  </td>
                  <td className="px-5 py-3">
                    {missingSales ? (
                      <span className="pill pill-danger">
                        <AlertCircle size={11} /> {t("storeFiles.statuses.missingSales")}
                      </span>
                    ) : f.status === "not_uploaded" ? (
                      <span className="pill pill-warning">{t("storeFiles.statuses.notUploaded")}</span>
                    ) : f.status === "uploaded" ? (
                      <span className="pill pill-success">{t("storeFiles.statuses.uploaded")}</span>
                    ) : f.status === "error" ? (
                      <span className="pill pill-danger" title={f.error_message}>
                        <AlertCircle size={11} /> {t("storeFiles.statuses.error")}
                      </span>
                    ) : (
                      <span className="pill pill-warning">
                        {t(`storeFiles.statuses.${normalizeStatus(f.status)}`, { defaultValue: f.status || "-" })}
                      </span>
                    )}
                    {f.error_message && (
                      <div className="text-[10px] text-rose-500 mt-1 max-w-[200px] truncate" title={f.error_message}>
                        {f.error_message}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <IconBtn icon={Download} title={t("storeFiles.actions.downloadGenerated")} onClick={() => downloadGenerated(f)} tone="blue" disabled={missingSales || !f.generated_filename} testid={`dl-gen-${f.id}`} />
                      <IconBtn icon={FileSpreadsheet} title={t("storeFiles.actions.downloadOriginal")} onClick={() => downloadOriginal(f)} tone="slate" disabled={missingSales || ["zero_sale", "store_closed"].includes(f.source)} testid={`dl-orig-${f.id}`} />
                      <IconBtn icon={RotateCw} title={t("storeFiles.actions.regenerate")} onClick={() => regenerate(f)} tone="green" disabled={missingSales || ["zero_sale", "store_closed"].includes(f.source)} testid={`re-${f.id}`} />
                      <IconBtn icon={Send} title={t("storeFiles.actions.reupload")} onClick={() => reupload(f)} tone="amber" disabled={missingSales || !f.generated_filename} testid={`ru-${f.id}`} />
                      {isAdmin && <IconBtn icon={Trash2} title={t("common.delete")} onClick={() => remove(f)} tone="rose" disabled={missingSales} testid={`del-${f.id}`} />}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

const inp =
  "w-full px-3.5 py-2 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm";

function IconBtn({ icon: Icon, title, onClick, tone, disabled, testid }) {
  const tones = {
    blue: "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100",
    slate: "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100",
    green: "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100",
    amber: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",
    rose: "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded-md border ${tones[tone]} disabled:opacity-40 disabled:cursor-not-allowed transition`}
    >
      <Icon size={14} />
    </button>
  );
}

function isMissingSales(file) {
  const status = String(file.status || "").toLowerCase().replace(/[\s-]+/g, "_");
  return status === "missing_sales" || status === "missing";
}

function normalizeOrigin(value) {
  const origin = String(value || "genuine").toLowerCase().replace(/[\s-]+/g, "_");
  return origin === "overwritten" || origin === "overwrite" ? "overwritten" : "genuine";
}

function normalizeStatus(value) {
  return String(value || "").toLowerCase().replace(/[\s-]+/g, "_");
}

function formatUploadDate(value, language) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const baseLanguage = String(language || "en").split("-")[0];
  const locale = { en: "en-GB", ms: "ms-MY", zh: "zh-SG" }[baseLanguage] || "en-GB";
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
